import React, { memo } from 'react'
import {
    Paper,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Grid,
    Typography,
    Divider,
    useTheme,
} from '@mui/material'
import { MoreVertIcon } from './Icons'
import { SurfaceProps } from './Surface.types'
import SurfaceSubtitle from './SurfaceSubtitle'
import SurfaceButton from './SurfaceButton'
import { SurfaceButtonVariant } from './SurfaceButton.types'
import SurfaceAvatar from './SurfaceAvatar'

function Surface({
    title,
    subtitle,
    icon,
    backButton,
    children,
    buttons,
    ellipsisCallback,
    ellipsisBuilder,
    footerActions,
    keepHeaderInline = false,
}: SurfaceProps) {
    const theme = useTheme()
    const renderButtons = (variant: SurfaceButtonVariant) => {
        return buttons
            ?.filter((button) => button.available)
            ?.filter((button) => button.variant === variant)
            ?.map((button, index) => (
                <Grid key={`button-${index}-${variant}`} item xs="auto">
                    <SurfaceButton {...button} />
                </Grid>
            ))
    }

    return (
        <>
            <Grid item xs={12} sx={{ marginBottom: 2 }}>
                <Grid
                    container
                    justifyContent="center"
                    alignContent="center"
                    alignItems="center"
                    spacing={1}
                >
                    <Grid item xs={keepHeaderInline ? true : 12} md>
                        <Grid
                            container
                            justifyContent="center"
                            alignContent="center"
                            alignItems="center"
                            spacing={1}
                        >
                            {icon && (
                                <Grid item xs="auto">
                                    <SurfaceAvatar
                                        icon={icon}
                                        backButton={backButton}
                                    />
                                </Grid>
                            )}
                            <Grid item xs>
                                <Grid
                                    container
                                    justifyContent="center"
                                    alignContent="center"
                                    alignItems="center"
                                >
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="h6"
                                            color={theme.palette.primary.main}
                                        >
                                            {title}
                                        </Typography>
                                    </Grid>
                                    {subtitle && (
                                        <SurfaceSubtitle subtitle={subtitle} />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {(buttons || !!ellipsisCallback || !!ellipsisBuilder) && (
                        <>
                            {!keepHeaderInline && (
                                <Grid
                                    item
                                    xs={12}
                                    sx={{ display: { xs: 'flex', md: 'none' } }}
                                >
                                    <Divider
                                        sx={(theme) => {
                                            return {
                                                width: '100%',
                                                height: '1px',
                                                bgcolor: theme.palette.divider,
                                                mb: theme.spacing(0.5),
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                            <Grid
                                item
                                xs={keepHeaderInline ? 'auto' : 12}
                                md="auto"
                            >
                                <Grid
                                    container
                                    justifyContent="flex-end"
                                    alignContent="center"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    <Grid item xs>
                                        <Grid
                                            container
                                            justifyContent="flex-end"
                                            alignContent="center"
                                            alignItems="center"
                                            spacing={2}
                                        >
                                            {renderButtons('secondary')}
                                            {renderButtons('primary')}
                                        </Grid>
                                    </Grid>
                                    {!!ellipsisCallback && (
                                        <Grid item xs="auto">
                                            <IconButton
                                                color="inherit"
                                                size="small"
                                                edge="start"
                                                onClick={(
                                                    event: React.MouseEvent<HTMLElement>
                                                ) =>
                                                    ellipsisCallback(
                                                        event.currentTarget
                                                    )
                                                }
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </Grid>
                                    )}
                                    {!!ellipsisBuilder && (
                                        <Grid item xs="auto">
                                            {ellipsisBuilder()}
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Grid>
            <Paper component={Card} variant="outlined">
                <CardContent>{children}</CardContent>
                {footerActions && <CardActions>{footerActions}</CardActions>}
            </Paper>
        </>
    )
}

export default memo(Surface)
